"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { PanoramaFeatureCollection } from "@/api-client";
import { API_URL } from "@/lib/config";
import { Waypoint, SavedRoute, TravelMode } from "./types";

const STORAGE_KEY = "route_builder_saved";

// OSRM public routing server — free, no API key, road-snapped geometry
// driving uses the main OSRM demo server; walking/cycling use OSM routing servers
const OSRM_BASE: Record<TravelMode, string> = {
  driving:   "https://router.project-osrm.org/route/v1/driving",
  walking:   "https://routing.openstreetmap.de/routed-foot/route/v1/foot",
  bicycling: "https://routing.openstreetmap.de/routed-bike/route/v1/bicycle",
  transit:   "https://router.project-osrm.org/route/v1/driving", // no transit — falls back to driving
};

function readSaved(): SavedRoute[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
  } catch {
    return [];
  }
}

function writeSaved(routes: SavedRoute[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(routes));
  } catch {}
}

function generateId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function useRouteBuilder(panoData: PanoramaFeatureCollection | null) {
  const [isOpen, setIsOpen]             = useState(false);
  const [routeName, setRouteName]       = useState("My Route");
  const [waypoints, setWaypoints]       = useState<Waypoint[]>([]);
  const [travelMode, setTravelMode]     = useState<TravelMode>("driving");
  const [savedRoutes, setSavedRoutes]   = useState<SavedRoute[]>([]);
  const [isAddingMode, setIsAddingMode] = useState(false);
  const [searchQuery, setSearchQuery]   = useState("");
  const [shareCopied, setShareCopied]   = useState(false);
  const [savedFlash, setSavedFlash]     = useState(false);
  const [tourCreating, setTourCreating] = useState(false);

  // Real routing state
  const [routeGeometry, setRouteGeometry] = useState<[number, number][] | null>(null);
  const [routeInfo, setRouteInfo]         = useState<{ distanceKm: number; durationSec: number } | null>(null);
  const [routeLoading, setRouteLoading]   = useState(false);
  const fetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load saved routes from localStorage on mount
  useEffect(() => { setSavedRoutes(readSaved()); }, []);

  // Restore route from ?route= URL param on mount
  useEffect(() => {
    try {
      const param = new URLSearchParams(window.location.search).get("route");
      if (!param) return;
      const decoded = JSON.parse(atob(param));
      if (decoded?.waypoints?.length) {
        setRouteName(decoded.name ?? "Shared Route");
        setWaypoints(decoded.waypoints);
        setTravelMode(decoded.travelMode ?? "driving");
        setIsOpen(true);
        // Strip param from URL without reload
        const url = new URL(window.location.href);
        url.searchParams.delete("route");
        window.history.replaceState(null, "", url.toString());
      }
    } catch {}
  }, []);

  // Fetch real route geometry from MapTiler Directions API
  const fetchRoute = useCallback(async (wps: Waypoint[], mode: TravelMode) => {
    if (wps.length < 2) {
      setRouteGeometry(null);
      setRouteInfo(null);
      return;
    }
    setRouteLoading(true);
    try {
      const coordStr = wps.map((w) => `${w.lng},${w.lat}`).join(";");
      const base = OSRM_BASE[mode];
      const url  = `${base}/${coordStr}?overview=full&geometries=geojson`;
      const res  = await fetch(url);
      if (!res.ok) throw new Error("routing failed");
      const data = await res.json();
      if (data.code !== "Ok") throw new Error("no route");
      const route = data.routes?.[0];
      if (!route) throw new Error("no route");
      const geometry: [number, number][] = route.geometry.coordinates;
      const distKm  = (route.distance as number) / 1000;
      const durSec  = route.duration as number;
      setRouteGeometry(geometry);
      setRouteInfo({ distanceKm: distKm, durationSec: durSec });
    } catch {
      // Fall back to straight-line — keep null so MapViewer uses waypoints directly
      setRouteGeometry(null);
      setRouteInfo(null);
    } finally {
      setRouteLoading(false);
    }
  }, []);

  // Debounce re-fetch whenever waypoints or travel mode change
  useEffect(() => {
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = setTimeout(() => {
      fetchRoute(waypoints, travelMode);
    }, 400);
    return () => { if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current); };
  }, [waypoints, travelMode, fetchRoute]);

  // Close adding mode when panel closes
  const close = useCallback(() => {
    setIsOpen(false);
    setIsAddingMode(false);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((v) => {
      if (v) setIsAddingMode(false);
      return !v;
    });
  }, []);

  const addWaypoint = useCallback((feature: { properties: { id: string; name?: string | null; thumbnail_url?: string | null }; geometry: { coordinates: number[] } }) => {
    const id = feature.properties.id;
    setWaypoints((prev) => {
      if (prev.some((w) => w.panoId === id)) return prev; // skip duplicate
      const [lng, lat] = feature.geometry.coordinates as [number, number];
      return [...prev, {
        panoId: id,
        name: feature.properties.name ?? "Untitled",
        lat,
        lng,
        thumbnailUrl: feature.properties.thumbnail_url ?? null,
      }];
    });
    setIsAddingMode(false);
  }, []);

  const removeWaypoint = useCallback((panoId: string) => {
    setWaypoints((prev) => prev.filter((w) => w.panoId !== panoId));
  }, []);

  const reorderWaypoints = useCallback((newOrder: Waypoint[]) => {
    setWaypoints(newOrder);
  }, []);

  const saveRoute = useCallback(() => {
    const route: SavedRoute = {
      id: generateId(),
      name: routeName,
      waypoints,
      travelMode,
      createdAt: new Date().toISOString(),
    };
    const updated = [route, ...readSaved()];
    writeSaved(updated);
    setSavedRoutes(updated);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
  }, [routeName, waypoints, travelMode]);

  const loadRoute = useCallback((route: SavedRoute) => {
    setRouteName(route.name);
    setWaypoints(route.waypoints);
    setTravelMode(route.travelMode);
  }, []);

  const deleteRoute = useCallback((id: string) => {
    const updated = readSaved().filter((r) => r.id !== id);
    writeSaved(updated);
    setSavedRoutes(updated);
  }, []);

  const openInMaps = useCallback((app: "google" | "waze" | "apple") => {
    if (waypoints.length === 0) return;
    const first = waypoints[0];
    const last  = waypoints[waypoints.length - 1];
    const mid   = waypoints.slice(1, -1);

    const dirflg: Record<TravelMode, string> = {
      driving: "d", walking: "w", bicycling: "c", transit: "r",
    };

    let url = "";
    if (app === "google") {
      const wps = mid.map((w) => `${w.lat},${w.lng}`).join("|");
      url = `https://www.google.com/maps/dir/?api=1`
        + `&origin=${first.lat},${first.lng}`
        + `&destination=${last.lat},${last.lng}`
        + (wps ? `&waypoints=${encodeURIComponent(wps)}` : "")
        + `&travelmode=${travelMode}`;
    } else if (app === "waze") {
      url = `https://waze.com/ul?ll=${last.lat},${last.lng}&navigate=yes`;
    } else {
      url = `https://maps.apple.com/?saddr=${first.lat},${first.lng}`
        + `&daddr=${last.lat},${last.lng}`
        + `&dirflg=${dirflg[travelMode] ?? "d"}`;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  }, [waypoints, travelMode]);

  const copyShareLink = useCallback(() => {
    const encoded = btoa(JSON.stringify({ name: routeName, waypoints, travelMode }));
    const url = `${window.location.origin}${window.location.pathname}?route=${encoded}`;
    navigator.clipboard.writeText(url).then(() => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    });
  }, [routeName, waypoints, travelMode]);

  const exportGPX = useCallback(() => {
    // Use real route geometry if available, otherwise fall back to waypoint coords
    const trkpts = routeGeometry
      ? routeGeometry.map(([lng, lat]) => `    <trkpt lat="${lat}" lon="${lng}"></trkpt>`).join("\n")
      : waypoints
          .map((w) => `    <trkpt lat="${w.lat}" lon="${w.lng}"><name>${w.name.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c] ?? c))}</name></trkpt>`)
          .join("\n");
    const gpx = `<?xml version="1.0" encoding="UTF-8"?>\n<gpx version="1.1" creator="360Photos">\n  <trk><name>${routeName}</name><trkseg>\n${trkpts}\n  </trkseg></trk>\n</gpx>`;
    const blob = new Blob([gpx], { type: "application/gpx+xml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${routeName.replace(/[^a-z0-9]/gi, "_")}.gpx`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [routeName, waypoints, routeGeometry]);

  const createTour = useCallback(async (onSuccess?: (colId: string) => void) => {
    if (waypoints.length === 0) return;
    setTourCreating(true);
    try {
      const colRes = await fetch(`${API_URL}/api/collections`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: routeName }),
      });
      if (!colRes.ok) throw new Error("Failed to create collection");
      const col = await colRes.json();
      const colId: string = col.id;

      for (let i = 0; i < waypoints.length; i++) {
        await fetch(
          `${API_URL}/api/collections/${colId}/panoramas/${waypoints[i].panoId}?sort_order=${i}`,
          { method: "POST", credentials: "include" },
        );
      }

      await fetch(`${API_URL}/api/collections/${colId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          route_show_routing: true,
          route_travel_mode: travelMode,
          route_speed: "average",
          route_camera_mode: "cinematic",
          route_vehicle_style:
            travelMode === "walking" ? "walking"
            : travelMode === "bicycling" ? "bike"
            : "car",
        }),
      });

      onSuccess?.(colId);
    } catch {
      // silent — button re-enables when tourCreating goes false
    } finally {
      setTourCreating(false);
    }
  }, [waypoints, routeName, travelMode]);

  // All user's active panoramas for the search list
  const allPanoramas = panoData?.features ?? [];

  return {
    isOpen, toggle, close,
    routeName, setRouteName,
    waypoints, addWaypoint, removeWaypoint, reorderWaypoints,
    travelMode, setTravelMode,
    savedRoutes, saveRoute, loadRoute, deleteRoute,
    isAddingMode, setIsAddingMode,
    searchQuery, setSearchQuery,
    shareCopied, savedFlash,
    openInMaps, copyShareLink, exportGPX,
    createTour, tourCreating,
    allPanoramas,
    routeGeometry, routeInfo, routeLoading,
  };
}
